import React from 'react';
import usePageMeta from '../hooks/usePageMeta';

const TermsOfService: React.FC = () => {
  usePageMeta({
    title: 'Terms of Service — BoostHiring',
    description:
      'BoostHiring Terms of Service. Read the terms and conditions for using the BoostHiring recruitment platform.',
    canonical: 'https://boosthiring.ca/terms',
  });

  return (
    <div className="min-h-screen bg-white">
      <div className="container mx-auto px-6 py-16 max-w-4xl">
        <header className="mb-12 border-b border-border-muted pb-8 flex items-start justify-between">
          <div>
            <h1 className="text-4xl font-bold text-primary mb-3">Terms of Service</h1>
            <p className="text-subtle text-lg">Last Updated: April 20th, 2026</p>
          </div>
          <button
            type="button"
            onClick={() => window.print()} // eslint-disable-line no-undef
            className="font-medium text-primary border border-border-muted rounded-lg px-4 py-2 hover:bg-bg-muted transition-colors print:hidden"
          >
            <span role="img" aria-label="Print">
              🖨️
            </span>{' '}
            Print
          </button>
        </header>

        <div className="prose max-w-none text-slate-700 leading-relaxed space-y-10">
          <div className="bg-bg-muted border border-border-muted rounded-xl p-6">
            <p className="font-semibold text-primary uppercase tracking-wide text-sm">
              PLEASE READ THIS TERMS OF SERVICE AGREEMENT CAREFULLY. BY USING THIS WEBSITE OR
              ORDERING SERVICES FROM THIS WEBSITE YOU AGREE TO BE BOUND BY ALL OF THE TERMS AND
              CONDITIONS OF THIS AGREEMENT.
            </p>
          </div>

          <p>
            BoostHiring is operated by Needl-it Inc. (&ldquo;BoostHiring&rdquo;, &ldquo;we&rdquo;,
            &ldquo;us&rdquo;).
          </p>
          <p>By using our platform, you agree to these Terms.</p>

          {/* Section 1 */}
          <section>
            <h2 className="text-2xl font-bold text-primary mb-4">1. Platform Overview</h2>
            <p>
              BoostHiring is a Software-as-a-Service (SaaS) marketplace platform that connects
              Employers with Recruitment Agencies and registered independent Recruiters.
            </p>
            <p className="mt-3">
              BoostHiring is not a recruitment agency and does not participate in hiring decisions.
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-2xl font-bold text-primary mb-4">2. Platform Role</h2>
            <p className="mb-3">BoostHiring:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>provides tools to manage hiring workflows</li>
              <li>does not recruit, employ, or screen candidates</li>
              <li>does not guarantee hiring outcomes</li>
            </ul>
            <p className="mt-3">All agreements are strictly between Employers and Recruiters.</p>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-2xl font-bold text-primary mb-4">3. Eligibility</h2>
            <p className="mb-3">You must:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>be at least 18 years old</li>
              <li>operate as a registered business (Employer or Agency)</li>
              <li>comply with applicable laws in Canada or the United States</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-2xl font-bold text-primary mb-4">4. Employer Terms</h2>
            <p className="mb-3">Employers agree to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>post genuine job opportunities</li>
              <li>define recruiter service fees</li>
              <li>pay fees only upon successful hire</li>
              <li>process payments within 30 days of candidate start date</li>
            </ul>
            <p className="mt-4 mb-3">Employers must not:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>bypass the platform</li>
              <li>
                hire candidates introduced via BoostHiring outside the platform within 12 months
              </li>
            </ul>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-2xl font-bold text-primary mb-4">5. Recruiter / Agency Terms</h2>
            <p className="mb-3">Recruiters agree to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>submit only qualified candidates</li>
              <li>obtain candidate consent before submission</li>
              <li>provide a minimum 90-day replacement guarantee</li>
              <li>invoice Employers accurately directly via platform on roles you filled</li>
            </ul>
            <p className="mt-4 mb-3">Recruiters operate under:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>subscription plans</li>
              <li>platform fee structure (if applicable)</li>
            </ul>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-2xl font-bold text-primary mb-4">
              6. Candidate Data &amp; Consent
            </h2>
            <ul className="list-disc pl-6 space-y-1 mb-4">
              <li>Recruiters retain full ownership of candidate data</li>
              <li>
                Recruiters are solely responsible for:
                <ul className="list-disc pl-6 mt-1 space-y-1">
                  <li>obtaining candidate consent</li>
                  <li>complying with privacy laws</li>
                </ul>
              </li>
            </ul>
            <p className="mb-3">BoostHiring:</p>
            <ul className="list-disc pl-6 space-y-1 mb-4">
              <li>does not verify consent</li>
              <li>does not control data collection</li>
              <li>acts only as a secure transmission platform</li>
            </ul>
            <p className="mb-3">BoostHiring is not liable for:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>unauthorized submissions</li>
              <li>privacy violations</li>
              <li>misuse of candidate data</li>
            </ul>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-2xl font-bold text-primary mb-4">7. Fees &amp; Payment</h2>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">7.1 Employer Payments</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Employers post jobs for free</li>
              <li>
                Employers agree to pay recruiters the Service Fee they define in the job posting
              </li>
              <li>Payment is due within 30 days of the candidate&rsquo;s start date</li>
              <li>
                Payment is made directly to the Recruiter according to invoice received via
                BoostHiring platform
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">
              7.2 Hiring Through the Platform
            </h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                A hire is considered valid if a candidate submitted through BoostHiring is hired
                within 12 months of submission
              </li>
              <li>
                In such cases, the Employer must pay the agreed Service Fee, regardless of hiring
                channel
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">
              7.3 Platform Fee (Recruiters)
            </h3>
            <p className="mb-3">
              Recruiters agree to pay BoostHiring a Platform Fee based on the total Service Fee
              earned from the Employer for each successful placement.
            </p>
            <p className="mb-3">Fee structure:</p>
            <ul className="list-disc pl-6 space-y-1 mb-4">
              <li>20% on the first $500</li>
              <li>10% on the portion between $500 and $10,000</li>
              <li>5% on any amount above $10,000</li>
            </ul>
            <div className="bg-bg-muted border border-border-muted rounded-lg p-4">
              <p className="text-sm">
                <strong>Example:</strong> If a Recruiter earns $7,500 &rarr; Platform Fee = $800 (+
                applicable taxes)
              </p>
            </div>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">
              7.4 Payment Terms (Recruiters)
            </h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>BoostHiring will issue an invoice after confirmation of placement</li>
              <li>Platform Fees are due within 10 days of invoice (Net 10)</li>
              <li>Payment methods will be specified on the invoice</li>
            </ul>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">7.5 No Circumvention</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                Employers and Recruiters agree to conduct all interactions through the platform
              </li>
              <li>Users may not bypass BoostHiring to avoid fees</li>
              <li>
                Any off-platform hiring of a submitted candidate within 12 months is still subject
                to fees
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">
              7.6 Active Engagement Obligations
            </h3>
            <p className="mb-3">Employers must:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>provide timely feedback on candidates</li>
              <li>notify Recruiters if a candidate was previously known within 12 months</li>
              <li>communicate hiring decisions through the platform</li>
            </ul>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">
              7.7 Independent Relationship
            </h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>BoostHiring is not a party to any agreement between Employers and Recruiters</li>
              <li>All hiring agreements, payments, and obligations remain between those parties</li>
            </ul>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">
              7.8 Payment Responsibility Disclaimer
            </h3>
            <ul className="list-disc pl-6 space-y-1 mb-4">
              <li>
                BoostHiring is not responsible for payment collection, enforcement, or guarantees
                between Employers and Recruiters
              </li>
              <li>All payments are made directly between Employers and Recruiters</li>
              <li>
                BoostHiring does not:
                <ul className="list-disc pl-6 mt-1 space-y-1">
                  <li>guarantee payment</li>
                  <li>act as escrow</li>
                  <li>enforce invoices</li>
                </ul>
              </li>
            </ul>
            <p>
              BoostHiring is not a party to any agreement between Users and does not assume any
              responsibility for the performance, actions, or obligations of any User.
            </p>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-2xl font-bold text-primary mb-4">8. Candidate Ownership</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>First submission timestamp determines ownership</li>
              <li>Duplicate candidates may be rejected</li>
            </ul>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-2xl font-bold text-primary mb-4">9. No Circumvention</h2>
            <p className="mb-3">Users agree not to:</p>
            <ul className="list-disc pl-6 space-y-1 mb-4">
              <li>bypass BoostHiring</li>
              <li>engage off-platform to avoid fees</li>
            </ul>
            <p className="mb-3">Violations may result in:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>account suspension</li>
              <li>financial penalties</li>
            </ul>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="text-2xl font-bold text-primary mb-4">10. Acceptable Use</h2>
            <p className="mb-3">Users must not:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>misuse the platform</li>
              <li>submit false information</li>
              <li>violate laws or third-party rights</li>
            </ul>
          </section>

          {/* Section 11 */}
          <section>
            <h2 className="text-2xl font-bold text-primary mb-4">11. Limitation of Liability</h2>
            <p className="mb-3">BoostHiring is not liable for:</p>
            <ul className="list-disc pl-6 space-y-1 mb-3">
              <li>hiring decisions</li>
              <li>candidate performance</li>
              <li>disputes between Users</li>
            </ul>
            <p>Liability is limited to fees paid to BoostHiring.</p>
          </section>

          {/* Section 12 */}
          <section>
            <h2 className="text-2xl font-bold text-primary mb-4">12. Termination</h2>
            <p className="mb-4">
              BoostHiring may suspend or terminate any account, or remove content, at any time if
              these Terms are violated or if misuse of the platform is suspected. We may attempt to
              provide notice before termination where reasonable, but are not obligated to do so.
            </p>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">12.2 User Cancellation</h3>
            <p>
              You may cancel your account at any time. Upon cancellation, BoostHiring may delete
              your account and related data from the platform.
            </p>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">
              12.3 Outstanding Obligations
            </h3>
            <p>
              Termination does not remove any obligations that arose before termination, including
              payment obligations, fees, or commitments related to submitted candidates. Termination
              or account closure does not release any party from obligations related to candidates
              introduced through the platform within the previous 12 months.
            </p>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">
              12.4 Active Engagements
            </h3>
            <p className="mb-3">
              If you are engaged in an active hiring process at the time of termination:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>all agreed terms between Employer and Recruiter remain valid</li>
              <li>any applicable fees remain payable</li>
            </ul>
          </section>

          {/* Section 13 */}
          <section>
            <h2 className="text-2xl font-bold text-primary mb-4">13. General (Miscellaneous)</h2>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">13.1 Governing Law</h3>
            <p>
              These Terms are governed by the laws of British Columbia, Canada. Any disputes will be
              handled in the courts of British Columbia.
            </p>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">13.2 Assignment</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                Users may not transfer or assign their rights or obligations under these Terms
              </li>
              <li>BoostHiring may assign or transfer its rights at any time</li>
            </ul>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">13.3 Severability</h3>
            <p>
              If any part of these Terms is found to be invalid or unenforceable, the remaining
              provisions will continue in full force.
            </p>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">13.4 Entire Agreement</h3>
            <p>
              These Terms, together with the Privacy Policy, form the entire agreement between you
              and BoostHiring.
            </p>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">13.5 Taxes</h3>
            <p>
              All fees are exclusive of applicable taxes (including GST), which will be added where
              required.
            </p>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">13.6 Language</h3>
            <p>
              These Terms are provided in English and will prevail in case of any inconsistencies
              with translated versions.
            </p>
          </section>

          {/* Section 14 */}
          <section>
            <h2 className="text-2xl font-bold text-primary mb-4">14. Updates</h2>
            <p>We may update these Terms at any time.</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
